<template>
	<Menu
		as="div"
		class="tiptap-blur-source relative inline-block text-left"
	>
		<div>
			<MenuButton
				:key="selectionUpdated"
				class="
					flex h-[24px] w-[125px] items-center justify-between rounded
					bg-won-sand-light px-2 hover:bg-won-green-light hover:text-won-green
				"
			>
				<span
					:style="{'fontFamily' : currentStyleFontFamily() }"
					class="inline-block truncate text-left text-sm font-medium text-gray-700"
				>
					{{ loaded ? currentFontFamily() : '...' }}
				</span>

				<ChevronDownIcon class="size-4" />
			</MenuButton>
		</div>
  
		<transition
			enter-active-class="transition ease-out duration-100"
			enter-from-class="transform opacity-0 scale-95"
			enter-to-class="transform opacity-100 scale-100"
			leave-active-class="transition ease-in duration-75"
			leave-from-class="transform opacity-100 scale-100"
			leave-to-class="transform opacity-0 scale-95"
		>
			<MenuItems
				class="
					absolute right-0 z-9999 mt-2 max-h-[195px] w-[150px] origin-top-right
					rounded bg-white shadow-sm ring-1 ring-black/5 focus:outline-hidden
				"
			>
				<div class="py-1">
					<MenuItem
						v-for="(value, index) in fontFamilies"
						:key="index"
						v-slot="{ active }"
						@click="() => applyFont(value)"
					>
						<a
							href="#"
							:class="[
								active ? 'bg-won-sand-light' : 'bg-white',
								{'bg-won-green-light font-semibold': isFontSelected(value)}
							]"
							class="block truncate px-4 py-1 text-sm text-gray-700"
							:style="{'fontFamily': value}"
						>
							{{ capitalizeFirstLetterEveryWord(value.replaceAll('"', '').split(',')[0]) }}
						</a>
					</MenuItem>
				</div>
			</MenuItems>
		</transition>
	</Menu>
</template>
  
<script setup>
import { computed } from 'vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/solid';

import { storeToRefs } from 'pinia';
import { fontFamilies , useBuilderStyles } from '../../store/builder-style.store';
import { useBuilder } from '../../store/builder.store';
import { useTiptapStore } from '../../store/tiptap.store';

import { capitalizeFirstLetterEveryWord } from '../../utils/helpers';

const tiptap = useTiptapStore();
const { editor, selectionUpdated, isFocused } = storeToRefs(tiptap);

const builderStyles = useBuilderStyles();
const builder = useBuilder();

const { loaded } = storeToRefs(builder);

const currentSelectionIsEmpty = computed(() => {
	return editor.value != null ? editor.value.view.state.selection.empty : true;
});

const isFontSelected = (font) => {
	return !currentSelectionIsEmpty.value
		? editor.value.isActive('textStyle', { fontFamily: font.replaceAll('"',  '') })
		: font === builderStyles.fontFamily;
};

const currentStyleFontFamily = () => {
	return !currentSelectionIsEmpty.value
		? editor.value.getAttributes('textStyle')?.fontFamily ?? builderStyles.fontFamily
		: builderStyles.fontFamily;
};

const currentFontFamily = () => {
	const currentSelectedTextFontFamily = editor?.value?.getAttributes('textStyle')?.fontFamily?.replaceAll('"',  '')?.split(',')[0];

	return !currentSelectionIsEmpty.value
		? currentSelectedTextFontFamily ?? builderStyles.currentFontFamily
		: builderStyles.currentFontFamily;
};

const applyFont = async (value) => {
	if (isFocused.value === false) {
		builderStyles.applyFont(value);
		return;
	}

	editor.value.chain().focus().setFontFamily(value.replaceAll('"','')).run();
};
</script>